import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import isArchived from 'archive/isArchived';
import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {ConnectionSchemaSelectorQuery} from './__generated/ConnectionSchemaSelectorQuery';
import {ConnectionSchema} from './ConnectionSchemaConstants';

interface BaseProps {
  className?: string;
  value: string | null;
  onChange: (connectionSchemaId: string | null) => void;
  disabled?: boolean;
  disabledTooltip?: string | null;
}

export const CONNECTION_SCHEMA_SELECTOR_QUERY: DocumentNode = gql`
  query ConnectionSchemaSelectorQuery @skipAudit {
    ...AllConnectionSchemasGraphQL
  }
  ${getFragmentWrapper('AllConnectionSchemas').fragment}
`;

export function QueryingConnectionSchemaSelector(props: BaseProps): JSX.Element {
  return (
    <BenchlingQuery<ConnectionSchemaSelectorQuery>
      query={CONNECTION_SCHEMA_SELECTOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({connectionSchemas}) => {
        const filteredSchemas = connectionSchemas.filter(
          (schema) => !isArchived(schema) || schema.id === props.value
        );

        const selectedSchema =
          filteredSchemas.find((connectionSchema) => connectionSchema.id === props.value) ?? null;

        return (
          <ConnectionSchemaSelector
            {...props}
            selectedSchema={selectedSchema}
            connectionSchemas={filteredSchemas}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface ConnectionSchemaSelectorProps extends BaseProps {
  connectionSchemas: Array<ConnectionSchema>;
  selectedSchema: ConnectionSchema | null;
}

function ConnectionSchemaSelector(props: ConnectionSchemaSelectorProps): JSX.Element {
  function schemaDisplayName(schema: ConnectionSchema): string {
    return isArchived(schema) ? `${schema.name} (Archived)` : schema.name;
  }

  const optionDatas = [
    {name: 'None', value: null},
    ...props.connectionSchemas.map((schema) => ({
      value: schema.id,
      name: schemaDisplayName(schema),
    })),
  ];

  const isDisabled =
    props.disabled || !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT});

  return (
    <SelectDropdown
      className={props.className}
      disabled={isDisabled}
      displayName={props.selectedSchema == null ? 'Select...' : schemaDisplayName(props.selectedSchema)}
      menu={
        <SearchableMenu
          onSelect={props.onChange}
          optionDatas={optionDatas}
          selectedValue={props.value}
          width={Menu.WIDTHS.FULL}
        />
      }
      error={props.disabled ? props.disabledTooltip : undefined}
      errorStyle={props.disabled ? SelectDropdownErrorStyle.TOOLTIP : undefined}
    />
  );
}

export default QueryingConnectionSchemaSelector;
